import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreVertical, FolderKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getProject, updateProject } from "@/lib/time-api";
import { ProjectFormDialog } from "@/components/time/ProjectFormDialog";
import { SettingsBreadcrumb } from "@/components/projects/SettingsBreadcrumb";
import { TaskList } from "@/components/projects/TaskList";
import { FormAssignments } from "@/components/projects/FormAssignments";
import { MemberList } from "@/components/projects/MemberList";

interface ProjectDetailProps {
  projectId: string;
  isAdmin: boolean;
}

export function ProjectDetail({ projectId, isAdmin }: ProjectDetailProps) {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const base = `/${orgId}/${slug}/settings/projects`;

  const { data, isLoading } = useQuery({
    queryKey: ["project", orgId, projectId],
    queryFn: () => getProject(projectId),
  });

  const project = data ?? null;

  async function handleUpdate(updateData: { name: string; color?: string }) {
    try {
      await updateProject(projectId, updateData);
      toast.success("Project updated");
      qc.invalidateQueries({ queryKey: ["project", orgId, projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update project");
    }
  }

  async function handleArchive() {
    try {
      await updateProject(projectId, { isArchived: true });
      toast.success("Project archived");
      qc.invalidateQueries({ queryKey: ["projects"] });
      navigate(base);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive project");
    }
    setArchiveOpen(false);
  }

  function handleMutate() {
    qc.invalidateQueries({ queryKey: ["project", orgId, projectId] });
  }

  if (isLoading || !project) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading project...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <SettingsBreadcrumb
          items={[
            { label: "Projects", href: base },
            { label: project.name },
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderKanban
              className="h-5 w-5 shrink-0"
              style={{ color: project.color ?? "#94A3B8" }}
            />
            <h2 className="text-lg font-semibold">{project.name}</h2>
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setArchiveOpen(true)}
                  >
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <TaskList
        projectId={projectId}
        tasks={project.tasks ?? []}
        isAdmin={isAdmin}
        onMutate={handleMutate}
      />

      <FormAssignments
        assignableType="project"
        assignableId={projectId}
        isAdmin={isAdmin}
      />

      <MemberList projectId={projectId} isAdmin={isAdmin} />

      <ProjectFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        onSubmit={handleUpdate}
        project={project}
      />

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive project?</AlertDialogTitle>
            <AlertDialogDescription>
              "{project.name}" will be removed from the active list. Existing time entries are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
